import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PUBLIC_API_BASE_URL } from '../../lib/config';

const CustomerLogin = () => {
  const [step, setStep] = useState(1); // 1 = email, 2 = OTP
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle autologin from email token
  useEffect(() => {
    const autoLoginToken = searchParams.get('token');

    if (autoLoginToken) {
      handleAutoLogin(autoLoginToken);
    }
  }, [searchParams]);

  // Pre-fill email from URL parameter or localStorage
  useEffect(() => {
    const urlEmail = searchParams.get('email');
    const storedEmail = localStorage.getItem('customer_email');

    if (urlEmail) {
      setEmail(urlEmail);
      // Store in localStorage for future use
      localStorage.setItem('customer_email', urlEmail);
    } else if (storedEmail) {
      setEmail(storedEmail);
    }
  }, [searchParams]);

  const handleAutoLogin = async (token) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${PUBLIC_API_BASE_URL}/customer-portal/autologin`, {
        token: token
      });

      if (response.data.status === 200) {
        // Store token and customer data
        localStorage.setItem('customer_token', response.data.token);
        localStorage.setItem('customer_data', JSON.stringify(response.data.data.customer));

        toast.success('Login successful!');

        // Check for redirect parameter and navigate accordingly
        const redirectPath = searchParams.get('redirect');
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate('/customer-portal/dashboard');
        }
      } else {
        setError(response.data.message || 'Invalid login link');
        toast.error(response.data.message || 'Invalid login link');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired login link. Please try manual login.');
      toast.error(err.response?.data?.message || 'Invalid or expired login link');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${PUBLIC_API_BASE_URL}/customer-portal/request-otp`, {
        email: email.trim()
      });

      if (response.data.status === 200) {
        toast.success('OTP sent to your email!');
        setStep(2);
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${PUBLIC_API_BASE_URL}/customer-portal/verify-otp`, {
        email: email.trim(),
        otp: otp.trim()
      });

      if (response.data.status === 200) {
        // Store token and customer data
        localStorage.setItem('customer_token', response.data.token);
        localStorage.setItem('customer_data', JSON.stringify(response.data.data.customer));

        toast.success('Login successful!');
        navigate('/customer-portal/dashboard');
      } else {
        setError(response.data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    setOtp('');
    setStep(1);
    toast.info('Please request OTP again');
  };

  // Show loading spinner when autologin is in progress
  if (loading && searchParams.get('token')) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Card className="shadow-lg text-center p-5" style={{ borderRadius: '15px', border: 'none' }}>
          <div className="mb-3">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <h5 className="text-muted">Logging you in...</h5>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow-lg" style={{ borderRadius: '15px', border: 'none' }}>
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold" style={{ color: '#764ba2' }}>Universal Client Hub</h2>
                  <p className="text-muted">Access your quotes and documents</p>
                </div>

                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                {step === 1 ? (
                  <Form onSubmit={handleRequestOTP}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '2px solid #e0e0e0'
                        }}
                      />
                      <Form.Text className="text-muted">
                        We'll send a verification code to this email
                      </Form.Text>
                    </Form.Group>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-100 red-bg text-white fw-bold"
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '16px'
                      }}
                    >
                      {loading ? 'Sending...' : 'Send Verification Code'}
                    </Button>
                  </Form>
                ) : (
                  <Form onSubmit={handleVerifyOTP}>
                    <div className="mb-3 text-center">
                      <p className="mb-2">Verification code sent to:</p>
                      <p className="fw-bold text-primary">{email}</p>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setStep(1)}
                        style={{ color: '#764ba2', textDecoration: 'none' }}
                      >
                        Change email
                      </Button>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Verification Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter 4-digit code"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        required
                        disabled={loading}
                        maxLength={4}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '2px solid #e0e0e0',
                          fontSize: '24px',
                          textAlign: 'center',
                          letterSpacing: '8px'
                        }}
                      />
                      <Form.Text className="text-muted">
                        Check your email for the 4-digit code
                      </Form.Text>
                    </Form.Group>

                    <Button
                      type="submit"
                      disabled={loading || otp.length !== 4}
                      className="w-100 red-bg text-white fw-bold mb-3"
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '16px'
                      }}
                    >
                      {loading ? 'Verifying...' : 'Verify & Login'}
                    </Button>

                    <div className="text-center">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={handleResendOTP}
                        disabled={loading}
                        style={{ color: '#764ba2', textDecoration: 'none' }}
                      >
                        Didn't receive code? Resend
                      </Button>
                    </div>
                  </Form>
                )}

                <div className="text-center mt-4">
                  <small className="text-muted">
                    Need help? Contact us at support@example.com
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CustomerLogin;
